import { useEffect, useState } from "react";
import { History } from "lucide-react";

/** Wave 4 item 5: "On this day".
 *
 *  Surfaces what the user captured 1 / 2 / 5 years ago today. The
 *  server narrates the items with local Ollama (qwen3:8b) so the
 *  section reads as a warm recollection rather than a raw list. The
 *  whole section self-hides when there is nothing from this day so
 *  it never adds empty chrome to the cockpit. */
interface OtdItem {
  when_label: string;
  text: string;
}
interface OtdResp {
  ok: boolean;
  items: OtdItem[];
  narrated: string;
  empty: string | null;
}

export function OnThisDay() {
  const [data, setData] = useState<OtdResp | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/today/on-this-day", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data || !data.ok || data.items.length === 0) return null;

  return (
    <section
      aria-label="On this day"
      className="glass rounded-2xl border border-amber-200/70 dark:border-amber-800/40 backdrop-blur bg-gradient-to-br from-amber-50/60 via-white/30 to-orange-50/40 dark:from-amber-900/15 dark:via-slate-800/20 dark:to-orange-900/10 p-5 sm:p-6 space-y-3"
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-300">
        <History className="w-4 h-4" />
        On this day
      </div>
      {data.narrated && (
        <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
          {data.narrated}
        </p>
      )}
      <ul className="space-y-2">
        {data.items.map((it, i) => (
          <li
            key={i}
            className="flex items-start gap-2.5 text-sm text-slate-600 dark:text-slate-300"
          >
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-200/70 dark:bg-amber-800/40 text-amber-800 dark:text-amber-200 flex-shrink-0 mt-0.5">
              {it.when_label}
            </span>
            <span className="flex-1">{it.text}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
